import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Collapse state for grouped table rows.
 * Resets when `groupField` changes.
 */
export const useGroupCollapse = (groupField) => {
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());

  useEffect(() => {
    setCollapsedKeys(new Set());
  }, [groupField]);

  const isCollapsed = useCallback(
    (key) => collapsedKeys.has(key),
    [collapsedKeys]
  );

  const toggleGroup = useCallback((key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const collapseAll = useCallback((keys) => {
    setCollapsedKeys(new Set(keys || []));
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedKeys(new Set());
  }, []);

  return { isCollapsed, toggleGroup, collapseAll, expandAll, collapsedKeys };
};

/** Banner: Grouped by X · Collapse all · Expand all · Ungroup */
export const GroupByBanner = ({
  groupTitle,
  groupKeys = [],
  onUngroup,
  onCollapseAll,
  onExpandAll,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-[#f0f9f4] px-4 py-2 text-sm text-gray-700">
    <span>
      Grouped by <strong>{groupTitle}</strong>
    </span>
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={() => onCollapseAll?.(groupKeys)}
        className="text-primary hover:underline"
      >
        Collapse all
      </button>
      <button type="button" onClick={() => onExpandAll?.()} className="text-primary hover:underline">
        Expand all
      </button>
      <button type="button" onClick={() => onUngroup?.()} className="text-primary hover:underline">
        Ungroup
      </button>
    </div>
  </div>
);

/** Clickable group header cell contents (chevron + label + count). */
export const GroupHeaderLabel = ({
  title,
  label,
  count,
  collapsed,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex max-w-full items-center gap-2 text-left text-sm font-semibold text-gray-800 hover:text-[#46955A]"
  >
    {collapsed ? (
      <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
    ) : (
      <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
    )}
    <span className="truncate">
      {title ? `${title}: ` : ''}
      {label}
    </span>
    <span className="shrink-0 font-normal text-gray-500">({count})</span>
  </button>
);
